/**
 * @fileoverview Scroll-Based Visibility Hook
 *
 * Provides scroll-responsive visibility state for headers/navs.
 * Hides on scroll down, shows on scroll up or when idle.
 *
 * @module hooks/useScrollHide
 */

import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Hook to show/hide elements based on scroll direction.
 *
 * Behavior:
 * - Hides when scrolling down (past 50px from top)
 * - Shows when scrolling up
 * - Shows after scroll stops (idle timeout)
 * - Can be disabled on specific routes
 *
 * @param threshold - Minimum scroll distance (px) to trigger hide/show (default: 10)
 * @param idleDelay - Milliseconds to wait before showing on idle (default: 500)
 * @param excludedRoutes - Routes where element always stays visible
 * @returns {boolean} True if element should be visible
 *
 * @example
 * const isNavVisible = useScrollHide(10, 500, ['/chat']);
 *
 * return (
 *   <nav className={isNavVisible ? 'translate-y-0' : 'translate-y-full'}>
 *     ...
 *   </nav>
 * );
 */
export function useScrollHide(threshold = 10, idleDelay = 500, excludedRoutes: string[] = []) {
  const [isVisible, setIsVisible] = useState(true);
  const lastScrollY = useRef(0);
  const idleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const location = useLocation();

  // Check if current route should disable scroll-hide behavior
  const isExcludedRoute = excludedRoutes.some(route => location.pathname.startsWith(route));

  useEffect(() => {
    // WHY: On excluded routes, always keep visible and skip scroll handling
    if (isExcludedRoute) {
      setIsVisible(true);
      return;
    }

    const showNav = () => setIsVisible(true);

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const scrollDiff = currentScrollY - lastScrollY.current;

      // Clear any existing idle timer
      if (idleTimer.current) {
        clearTimeout(idleTimer.current);
      }

      // WHY: Only trigger on significant scroll to avoid jitter
      if (Math.abs(scrollDiff) >= threshold) {
        if (scrollDiff > 0 && currentScrollY > 50) {
          // Scrolling down past initial area - hide
          setIsVisible(false);
        } else {
          // Scrolling up - show
          setIsVisible(true);
        }
        lastScrollY.current = currentScrollY;
      }

      // WHY: Show nav after user stops scrolling for better UX
      idleTimer.current = setTimeout(showNav, idleDelay);
    };

    // WHY: Passive listener for scroll performance
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (idleTimer.current) {
        clearTimeout(idleTimer.current);
      }
    };
  }, [threshold, idleDelay, isExcludedRoute]);

  return isVisible;
}
